import instance from '../axios'

/**
 * Merged comment item returned by GET /comments/{mediaType}/{id}.
 * Source 'TMDB' items are live TMDB reviews (read-only, never stored in our DB);
 * Source 'User' items are Movieception comments.
 * Responses are camelCased by ASP.NET Core's default JSON policy.
 */
export type CommentItem = {
  source: 'User' | 'TMDB'
  id?: number | null // DB comment id (User source only)
  tmdbReviewId?: string | null // TMDB review id (TMDB source only)
  userId?: number | null // lets the client mark "own" comments
  authorName: string
  authorUsername?: string | null // profile URL handle (User source only)
  authorAvatarUrl?: string | null
  content: string
  authorRating?: number | null // TMDB reviewer's own score
  createdOnUtc: string
  modifiedOnUtc?: string | null
  // One-level threading (User source only; TMDB reviews are read-only and flat)
  parentCommentId?: number | null
  replies?: CommentItem[]
}

// Request payloads keep PascalCase like the other *_api payload types
// (server-side model binding is case-insensitive).
export type AddCommentPayload = {
  Comments_MediaType: 'Movie' | 'Show' | 'Episode'
  Comments_TmdbMediaId: number
  Comments_TmdbEpisodeId?: number
  Comments_SeasonNumber?: number
  Comments_EpisodeNumber?: number
  // Reply to a top-level comment; the server inherits media fields from the parent
  Comments_ParentCommentId?: number
  Comments_Content: string
}

// Comment list plus whether more TMDB review pages can be loaded
export type CommentsFeed = {
  comments: CommentItem[]
  tmdbHasMore: boolean
}

const commentsApi = {
  // Merged TMDB reviews (page 1) + user comments, newest first. Public endpoint.
  getForMedia: (mediaType: 'movie' | 'show', tmdbMediaId: number) =>
    instance.get<CommentsFeed>(`/comments/${mediaType}/${tmdbMediaId}`),
  // Further TMDB review pages for "load more". Public endpoint.
  getMoreTmdbReviews: (mediaType: 'movie' | 'show', tmdbMediaId: number, page: number) =>
    instance.get<CommentsFeed>(`/comments/${mediaType}/${tmdbMediaId}/tmdb?page=${page}`),
  // Episode comments are DB-only (TMDB has no per-episode reviews). Public endpoint.
  getForEpisode: (tmdbEpisodeId: number) =>
    instance.get<CommentsFeed>(`/comments/episode/${tmdbEpisodeId}`),
  add: (payload: AddCommentPayload) => instance.post(`/comments`, payload),
  update: (commentId: number, content: string) =>
    instance.put(`/comments/${commentId}`, { Comments_Content: content }),
  delete: (commentId: number) => instance.delete(`/comments/${commentId}`),
}

export default commentsApi
